/**
 * NOTE on redux:
 *  - `state.coupons.applied` -> `state.customerCoupons.applied` (same
 *    business-vs-customer namespace fix applied everywhere else).
 *  - `coupons/cancelApplied` duck doesn't exist anywhere — only the backing
 *    API call (coupons/api.js -> cancelAppliedCoupon) exists, with no
 *    action/saga wrapping it. Called directly from this component as a
 *    temporary bridge, same pattern as applyCoupon/reserveCoupon in
 *    CouponsDetails/CouponAction.js.
 */
import React, { useCallback, useEffect, useState } from 'react';

import CSSModule from 'react-css-modules';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Spinner,UsedCoupon } from 'components';
import { cancelAppliedCoupon } from 'coupon-common/src/modules/coupons/api';
import { actions } from 'coupon-common/src/modules/customerCoupons/applied';

import { Empty as EmptyScreen, Error as ErrorScreen, Loading as LoadingScreen } from '../States';
import messages from './messages';
import styles from './UsedCoupons.module.scss';

const CouponList = ({ filterFunction, onCouponPress, enableButton }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const appliedState = useSelector(state => state.customerCoupons.applied);
  const { fetching, fetched, items, result, error } = appliedState;
  const { results = [], nextPage } = result || {};

  const [toast, setToast] = useState(null);

  const list = results.map(res => items[res]).filter(Boolean).filter(filterFunction);

  const getApplyCouponList = useCallback(
    params => {
      dispatch(actions.getApplyCouponList(params));
    },
    [dispatch],
  );

  useEffect(() => {
    getApplyCouponList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadMore = () => {
    if (nextPage) {
      getApplyCouponList({ page: nextPage });
    }
  };

  const onCancelApplied = async id => {
    try {
      await cancelAppliedCoupon(id);
      getApplyCouponList();
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error';
      setToast(intl.formatMessage(messages.cantCancelErrorMessage, { errorMessage }));
      setTimeout(() => setToast(null), 3500);
    }
  };

  if (error) {
    return <ErrorScreen error={error} refreshing={fetching} onRefresh={getApplyCouponList} />;
  }

  if (fetching && !fetched) {
    return <LoadingScreen />;
  }

  if (list.length === 0) {
    return <EmptyScreen refreshing={fetching} onRefresh={getApplyCouponList} />;
  }

  return (
    <div styleName="page">
      <div styleName="list">
        {list.map((item, index) => (
          <UsedCoupon
            key={item.id}
            first={index === 0}
            data={item}
            onCancel={() => onCancelApplied(item.id)}
            onPress={() => onCouponPress(item)}
            enableButton={enableButton}
          />
        ))}
      </div>

      {nextPage && (
        <div styleName="load-more-row">
          <button type="button" styleName="load-more-btn" onClick={handleLoadMore} disabled={fetching}>
            {fetching ? <Spinner show size="sm" /> : 'Load more'}
          </button>
        </div>
      )}

      {toast && <div styleName="toast">{toast}</div>}
    </div>
  );
};

export default CSSModule(CouponList, styles, { allowMultiple: true });